import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import PermissionGate from './PermissionGate';
import {
  LayoutDashboard,
  Package,
  Calendar,
  ListOrdered,
  CheckCircle,
  Store,
  Users,
  Tag,
  TrendingUp,
  BarChart3,
  Settings as SettingsIcon,
  HelpCircle,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Image,
  Menu as MenuIcon,
  Newspaper,
  Tags as TagsIcon,
  FileText,
  Plug,
  GraduationCap,
  Wallet,
  Truck,
  ClipboardList,
  Star,
  Import,
  Megaphone,
  PieChart,
  UserCircle,
  Shield,
  Bell,
  Building2,
  CreditCard,
  Receipt,
  Globe,
  Wrench
} from 'lucide-react';
import { useCurrency, SUPPORTED_CURRENCIES } from '../context/CurrencyContext';

// Module-level variable to persist scroll position across re-renders/re-mounts
let savedScrollTop = 0;

const Sidebar = () => {
  const location = useLocation();
  const { isAdmin, isSuperAdmin, isRestrictedTeamMember, hasGranularAccess } = useAuth();
  const { branding } = useTheme();
  const { currency, setCurrency, currencyInfo } = useCurrency();
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const sidebarRef = useRef(null);
  
  // Define which paths belong to which expandable section (for vendor nav)
  // Only include paths that are CHILDREN of expandable menus
  const getSectionForPath = useCallback((path) => {
    // Orders section - vendor and admin orders
    if (path === '/orders' || path.startsWith('/orders/') || path === '/vendor/requests' || 
        path === '/vendor/tax-invoices' || path === '/bookings' || path.startsWith('/bookings/') ||
        path === '/admin/tax-invoices' || path === '/leads' || path.startsWith('/leads/') ||
        path === '/shipping' || path.startsWith('/shipping/')) {
      return 'orders';
    }
    // Listings section - listings pages, pending approvals, master catalog, option templates, create-listing
    if (path === '/listings' || path.startsWith('/listings/') || 
        path === '/admin/pending-approvals' || path.startsWith('/admin/pending-approvals/') ||
        path === '/master-catalog' || path.startsWith('/master-catalog/') ||
        path === '/option-templates' || path.startsWith('/option-templates/') ||
        path.startsWith('/create-listing/') || path.startsWith('/edit-listing/')) {
      return 'listings';
    }
    // Marketing section children - vendor and admin marketing
    if (path.startsWith('/marketing/') || path === '/promotions' || path === '/vendor/advertising' ||
        path.startsWith('/admin/marketing/') || path === '/offers') {
      return 'marketing';
    }
    // Customers section children - vendor and admin customers
    if (path === '/customers' || path === '/customer-segments' || 
        path.startsWith('/admin/customers') || path.startsWith('/customer')) {
      return 'customers';
    }
    // Store Setup section children (myBusiness)
    if (path === '/vendor/store-profile' || path === '/vendor/locations' || path === '/vendor/compliance' || 
        path === '/settings/shipping' || path === '/vendor/operational') {
      return 'myBusiness';
    }
    // Admin Vendors section
    if (path.includes('/vendors') || path.includes('/admin/vendor-compliance') || path.includes('/admin/payouts') || 
        path.includes('/vendor-commissions') || path.includes('/admin/commission-invoices') || path.includes('/admin/payout-tax-logic') ||
        path.includes('/admin/lead-pricing') || path.includes('/subscription-plans') || path.includes('/admin/campaigns') ||
        path === '/admin/verification' || path.startsWith('/admin/verification/')) {
      return 'vendors';
    }
    // CMS section
    if (path.includes('/categories') || path.includes('/tags') || path.includes('/collections') || 
        path.includes('/pages') || path.includes('/menus') || path.includes('/blog') || path.includes('/admin/taxonomy') ||
        path === '/media' || path.startsWith('/media/')) {
      return 'cms';
    }
    // Integrations section
    if (path.includes('/admin/shopify-sync') || path.includes('/admin/integration-configs') || path.includes('/admin/webhook-manager') || path.includes('/admin/sales-channels') || path.includes('/admin/vim') || path.includes('/admin/storefront-demo')) {
      return 'integrations';
    }
    // Security section children
    if (path.includes('/admin/session-security') || path.includes('/admin/security') || path.includes('/admin/active-sessions') || path.includes('/admin/locked-accounts')) {
      return 'security';
    }
    // Analytics section children
    if (path === '/analytics' || path.includes('/admin/revenue-analytics') || path.includes('/admin/tax-management')) {
      return 'analytics';
    }
    // Return null for standalone items - this will collapse any open section
    return null;
  }, []);

  // Derive section from current path
  const pathSection = useMemo(() => getSectionForPath(location.pathname), [location.pathname, getSectionForPath]);
  
  // Track manually toggled section (only used when user explicitly clicks to expand/collapse)
  const [manualToggle, setManualToggle] = useState({ section: null, path: null });
  
  // Scroll position persistence using module-level variable
  const handleScroll = useCallback(() => {
    if (sidebarRef.current) {
      savedScrollTop = sidebarRef.current.scrollTop;
    }
  }, []);
  
  // Restore scroll position synchronously before paint
  useLayoutEffect(() => {
    if (sidebarRef.current && savedScrollTop > 0) {
      sidebarRef.current.scrollTop = savedScrollTop;
    }
  });
  
  // Determine active section: use manual toggle only if we're on the same path where toggle happened
  const activeSection = useMemo(() => {
    // If user manually toggled on current path, use that
    if (manualToggle.path === location.pathname) {
      return manualToggle.section;
    }
    // Otherwise, use path-derived section
    return pathSection;
  }, [manualToggle, location.pathname, pathSection]);

  const isActive = (path) => {
    // Exact match for dashboard
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Exact match for standalone items to prevent false positives
    if (path === '/orders') {
      return location.pathname === '/orders';
    }
    if (path === '/customers') {
      return location.pathname === '/customers';
    }
    // For other paths, use startsWith but ensure it's a proper segment match
    if (location.pathname === path) {
      return true;
    }
    // Check if it's a proper path prefix (not partial match)
    return location.pathname.startsWith(path + '/');
  };

  const toggleSection = (sectionKey) => {
    // Toggle section - if clicking the same one, close it; otherwise open the new one
    setManualToggle(current => ({
      section: current.section === sectionKey ? null : sectionKey,
      path: location.pathname
    }));
  };

  // Admin Navigation - keep as is for now, focus on vendor improvements
  const adminNavigation = [
    { label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard', single: true, permission: 'view_dashboard', key: 'dashboard' },
    {
      label: 'Vendors',
      icon: Store,
      key: 'vendors',
      permission: 'manage_vendors',
      children: [
        { to: '/vendors', label: 'All Vendors', permission: 'manage_vendors', sub: 'all_vendors' },
        { to: '/admin/vendor-compliance', label: 'Compliance Review', permission: 'manage_vendors', sub: 'compliance' },
        { to: '/admin/verification', label: 'Verification Requests', permission: 'manage_vendors', sub: 'verification' },
        { to: '/vendor-commissions', label: 'Vendor Commissions', permission: 'manage_vendors', superAdminOnly: true, sub: 'commissions' },
        { to: '/admin/commission-invoices', label: 'Commission Invoices', permission: 'manage_vendors', superAdminOnly: true, testid: 'nav-admin-commission-invoices', sub: 'commission_invoices' },
        // DEPRECATED: Commission Settings removed - managed in Subscription Plans
        { to: '/admin/lead-pricing', label: 'Lead Pricing', permission: 'manage_vendors', sub: 'lead_pricing' },
        { to: '/subscription-plans', label: 'Subscription Plans', permission: 'manage_subscriptions', superAdminOnly: true, sub: 'subscriptions' },
        { to: '/admin/vendors/billing', label: 'Billing', permission: 'manage_vendors', superAdminOnly: true, testid: 'nav-admin-billing', sub: 'billing' },
        { to: '/admin/campaigns', label: 'Vendor Campaigns', permission: 'manage_campaigns', sub: 'campaigns' }
      ]
    },
    // iter-251 IA — dedicated Finance & Accounts group. Pure
    // information-architecture move (no route / permission / API
    // changes). Pages relocated from Vendors and Analytics. Future
    // additions (Revenue Recognition, Refund Reconciliation, GST /
    // GSTR-8, Audit History, Financial Analytics, Finance Dashboard)
    // are documented in /app/memory/SCHEMA_MODERNIZATION_PRD.md §15
    // and will be added here when those modules exist.
    {
      label: 'Finance & Accounts',
      icon: Receipt,
      key: 'finance',
      permission: 'manage_payouts',
      superAdminOnly: true,
      children: [
        { to: '/admin/tools/financial-integrity', label: 'Financial Integrity', permission: 'manage_payouts', superAdminOnly: true, testid: 'sidebar-link-financial-integrity', sub: 'financial_integrity' },
        { to: '/admin/finance-register', label: 'Finance Register', permission: 'view_vendor_commissions', superAdminOnly: true, testid: 'sidebar-link-finance-register', sub: 'finance_register' },
        { to: '/admin/payouts', label: 'Vendor Payouts', permission: 'manage_payouts', superAdminOnly: true, sub: 'vendor_payouts' },
        { to: '/admin/payout-health', label: 'Payout Health Monitor', permission: 'manage_payouts', superAdminOnly: true, testid: 'sidebar-link-payout-health', sub: 'payout_health' },
        { to: '/admin/reconciliation', label: 'Razorpay Reconciliation', permission: 'manage_payouts', superAdminOnly: true, testid: 'sidebar-link-reconciliation', sub: 'razorpay_reconciliation' },
        { to: '/admin/payout-tax-logic', label: 'Payout & Tax Logic', permission: 'manage_settings', superAdminOnly: true, sub: 'payout_tax_logic' },
        { to: '/admin/revenue-analytics', label: 'Revenue', permission: 'view_analytics', sub: 'revenue' },
        { to: '/admin/tax-management', label: 'Tax Management', permission: 'view_analytics', sub: 'tax_management' }
      ]
    },
    {
      label: 'Listings',
      icon: Package,
      key: 'listings',
      permission: 'manage_listings',
      children: [
        { to: '/listings', label: 'All Listings', permission: 'manage_listings', sub: 'all_listings' },
        { to: '/admin/master-catalog', label: 'Master Catalog', permission: 'manage_listings', sub: 'master_catalog' },
        { to: '/admin/inventory/master', label: 'Master Inventory', permission: 'manage_listings', sub: 'master_inventory' },
        { to: '/admin/tools/ssot-audit', label: 'SSOT Audit', permission: 'manage_listings', superAdminOnly: true, testid: 'nav-ssot-audit', sub: 'ssot_audit' },
        { to: '/admin/tools/price-reconciliation', label: 'Price Reconciliation', permission: 'manage_listings', superAdminOnly: true, testid: 'nav-price-reconciliation', sub: 'price_reconciliation' },
        { to: '/admin/approvals', label: 'Pending Approvals', permission: 'approve_listings', sub: 'approvals' },
        { to: '/admin/custom-option-templates', label: 'Custom Option Templates', permission: 'manage_listings', sub: 'custom_options' }
      ]
    },
    {
      label: 'Orders',
      icon: ListOrdered,
      key: 'orders',
      permission: 'view_orders',
      children: [
        { to: '/orders', label: 'All Orders', permission: 'view_orders', sub: 'all_orders' },
        { to: '/orders/leads', label: 'All Leads', permission: 'view_orders', sub: 'leads' },
        { to: '/orders/shipping', label: 'Shipping', permission: 'view_shipping', sub: 'shipping' },
        { to: '/admin/shipping-module', label: 'Shipping Module', permission: 'edit_settings', sub: 'shipping_module' },
        { to: '/admin/settings/shipping', label: 'Shipping Settings', permission: 'edit_settings', sub: 'shipping_settings' },
        { to: '/admin/tax-invoices', label: 'Tax Invoices', permission: 'view_orders', sub: 'invoices' }
      ]
    },
    {
      label: 'Customers',
      icon: Users,
      key: 'customers',
      permission: 'view_customers',
      children: [
        { to: '/customers', label: 'All Customers', permission: 'view_customers', sub: 'all_customers' },
        { to: '/customer-segments', label: 'Segments', permission: 'manage_segments', sub: 'segments' }
      ]
    },
    {
      label: 'Marketing',
      icon: TrendingUp,
      key: 'marketing',
      permission: 'manage_marketing',
      children: [
        { to: '/admin/marketing/ad-approvals', label: 'Ad Approvals', permission: 'manage_campaigns', sub: 'ad_approvals' },
        { to: '/admin/marketing/all-campaigns', label: 'All Campaigns', permission: 'manage_campaigns', sub: 'campaigns' },
        { to: '/offers', label: 'Discounts & Offers', permission: 'manage_discounts', sub: 'discounts' },
        { to: '/marketing/promotions', label: 'Promotions', permission: 'manage_promotions', sub: 'promotions' },
        { to: '/marketing/ad-inventory', label: 'Ad Inventory', permission: 'manage_ads', sub: 'ad_inventory' },
        { to: '/marketing/campaigns', label: 'Ad Campaigns', permission: 'manage_campaigns', sub: 'ad_campaigns' },
        { to: '/marketing/builder', label: 'Campaign Builder', permission: 'send_emails', sub: 'builder' }
      ]
    },
    { 
      label: 'Analytics', 
      icon: BarChart3, 
      key: 'analytics',
      permission: 'view_analytics',
      children: [
        { to: '/analytics', label: 'Overview', permission: 'view_analytics', sub: 'overview' },
        { to: '/admin/onboarding-funnel', label: 'Onboarding Funnel', permission: 'manage_vendors', sub: 'funnel' }
      ]
    },
    {
      label: 'Communication',
      icon: MessageSquare,
      key: 'communication',
      permission: 'manage_settings',
      children: [
        { to: '/admin/communication-hub', label: 'Communication Hub', permission: 'manage_settings', sub: 'hub' },
        { to: '/admin/notifications', label: 'Notifications', permission: 'manage_settings', sub: 'notifications' }
      ]
    },
    {
      label: 'CMS',
      icon: FileText,
      key: 'cms',
      permission: 'manage_content',
      children: [
        { to: '/categories', label: 'Categories', permission: 'manage_categories', sub: 'categories' },
        { to: '/admin/filter-sets', label: 'Filter Sets', permission: 'manage_categories', sub: 'filter_sets' },
        { to: '/admin/taxonomy', label: 'Taxonomy Library', permission: 'manage_categories', sub: 'taxonomy' },
        { to: '/tags', label: 'Tags', permission: 'manage_tags', sub: 'tags' },
        { to: '/collections', label: 'Collections', permission: 'manage_collections', sub: 'collections' },
        { to: '/media', label: 'Media Gallery', permission: 'manage_media', sub: 'media' },
        { to: '/pages', label: 'Pages', permission: 'manage_pages', sub: 'pages' },
        { to: '/menus', label: 'Menus', permission: 'manage_menus', sub: 'menus' },
        { to: '/blog', label: 'Blog', permission: 'manage_blog', sub: 'blog' }
      ]
    },
    {
      label: 'Integrations',
      icon: Plug,
      key: 'integrations',
      permission: 'manage_integrations',
      superAdminOnly: true,  // Only super admins can access integrations
      children: [
        { to: '/admin/shopify-sync', label: 'Shopify Sync', permission: 'manage_shopify', sub: 'shopify_sync' },
        { to: '/admin/integration-configs', label: 'API Configs', permission: 'manage_integrations', sub: 'api_configs' },
        { to: '/admin/webhook-manager', label: 'Webhooks', permission: 'manage_integrations', sub: 'webhooks' },
        { to: '/admin/sales-channels', label: 'Sales Channels', permission: 'manage_integrations', sub: 'sales_channels' },
        { to: '/admin/vim', label: 'Ad Inventory (VIM)', permission: 'manage_settings', sub: 'ad_inventory_vim' },
        { to: '/admin/storefront-demo', label: 'Storefront Demo', permission: 'manage_settings', sub: 'storefront_demo' }
      ]
    },
    {
      label: 'Training Academy',
      icon: GraduationCap,
      to: '/admin/training',
      single: true,
      permission: 'manage_settings',
      key: 'training'
    },
    {
      label: 'Notifications',
      icon: Bell,
      to: '/admin/notifications',
      single: true,
      permission: 'all',
      key: 'communication',
      sub: 'notifications'
    },
    {
      label: 'Team & Roles',
      icon: Users,
      to: '/admin/team',
      single: true,
      permission: 'manage_settings',
      superAdminOnly: true,  // Only super admins can manage team
      key: 'team'
    },
    {
      label: 'Security',
      icon: Shield,
      key: 'security',
      permission: 'manage_settings',
      superAdminOnly: true,
      children: [
        { to: '/admin/session-security', label: 'Session Settings', permission: 'manage_settings', sub: 'session_settings' },
        { to: '/admin/security', label: 'IP Whitelist', permission: 'manage_settings', sub: 'ip_whitelist' },
        { to: '/admin/active-sessions', label: 'Active Sessions', permission: 'manage_settings', sub: 'active_sessions' },
        { to: '/admin/locked-accounts', label: 'Locked Accounts', permission: 'manage_settings', sub: 'locked_accounts' }
      ]
    },
    {
      label: 'Developer Tools',
      icon: Wrench,
      key: 'developer_tools',
      permission: 'manage_settings',
      superAdminOnly: true,
      children: [
        { to: '/admin/tools/maintenance-runner', label: 'Maintenance Runner', permission: 'manage_settings', superAdminOnly: true, testid: 'nav-maintenance-runner', sub: 'maintenance_runner' }
      ]
    },
    {
      label: 'Settings',
      icon: SettingsIcon,
      to: '/settings',
      single: true,
      permission: 'view_settings',
      superAdminOnly: true,  // Only super admins can access settings
      key: 'settings'
    },
    {
      label: 'Help & Support',
      icon: HelpCircle,
      to: '/help',
      single: true,
      permission: 'all'
    },
    {
      label: 'Messaging',
      icon: MessageSquare,
      to: '/messaging',
      single: true,
      permission: 'all'
    }
  ];

  // IMPROVED VENDOR NAVIGATION - Reorganized with sections
  const vendorNavigation = [
    // QUICK ACCESS
    { 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      to: '/dashboard', 
      single: true, 
      permission: 'all' 
    },
    
    // SECTION HEADER: DAILY OPERATIONS
    { type: 'section', label: 'Daily Operations' },
    
    {
      label: 'Orders',
      icon: ListOrdered,
      key: 'orders',
      permission: 'all',
      children: [
        { to: '/orders', label: 'All Orders', permission: 'all' },
        { to: '/orders/leads', label: 'Leads & Inquiries', permission: 'all' },
        { to: '/vendor/requests', label: 'Customer Requests', badge: true, permission: 'all' },
        { to: '/orders/shipping', label: 'Pack & Ship', permission: 'all' },
        { to: '/vendor/tax-invoices', label: 'Tax Invoices', icon: FileText, permission: 'all' }
      ]
    },
    { 
      label: 'Bookings', 
      icon: Calendar, 
      to: '/bookings', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Subscribers', 
      icon: Users, 
      to: '/vendor/subscribers', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Payouts', 
      icon: Wallet, 
      to: '/vendor/payouts', 
      single: true, 
      permission: 'all' 
    },
    {
      label: 'Commission Invoices',
      icon: Receipt,
      to: '/vendor/commission-invoices',
      single: true,
      permission: 'all'
    },
    
    // SECTION HEADER: CATALOG
    { type: 'section', label: 'Catalog' },
    
    { 
      label: 'Listings', 
      icon: Package, 
      to: '/listings', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Inventory', 
      icon: ClipboardList, 
      to: '/orders/inventory', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Reviews', 
      icon: Star, 
      to: '/listings/reviews', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Import Catalog', 
      icon: Import, 
      to: '/catalogue-pull', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Media Gallery', 
      icon: Image, 
      to: '/media', 
      single: true, 
      permission: 'all' 
    },
    
    // SECTION HEADER: GROW
    { type: 'section', label: 'Grow' },
    
    { 
      label: 'Analytics', 
      icon: PieChart, 
      to: '/analytics', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Discounts', 
      icon: Tag, 
      to: '/offers', 
      single: true, 
      permission: 'all' 
    },
    {
      label: 'Marketing',
      icon: Megaphone,
      key: 'marketing',
      permission: 'all',
      children: [
        { to: '/marketing/campaigns', label: 'Ad Campaigns', permission: 'all' },
        { to: '/promotions', label: 'Promotions', permission: 'all' },
        { to: '/vendor/advertising', label: 'Advertising', permission: 'all' },
        { to: '/marketing/builder', label: 'Campaign Builder', permission: 'all' }
      ]
    },
    
    // SECTION HEADER: LEARN
    { type: 'section', label: 'Learn' },
    
    {
      label: 'Training Academy',
      icon: GraduationCap,
      to: '/vendor/training',
      single: true,
      permission: 'all',
      highlight: true
    },
    { 
      label: 'Help & Support', 
      icon: HelpCircle, 
      to: '/help', 
      single: true, 
      permission: 'all' 
    },
    
    // SECTION HEADER: SETTINGS
    { type: 'section', label: 'Settings' },
    
    {
      label: 'Store Setup',
      icon: Store,
      key: 'myBusiness',
      permission: 'all',
      children: [
        { to: '/vendor/store-profile', label: 'Store Profile', permission: 'all' },
        { to: '/vendor/brand-registry', label: 'Brand Registry', permission: 'all' },
        { to: '/vendor/compliance', label: 'Compliance & KYC', permission: 'all' },
        { to: '/vendor/locations', label: 'Locations', permission: 'all' },
        { to: '/settings/shipping', label: 'Shipping Setup', permission: 'all' },
        { to: '/vendor/operational', label: 'Operations', permission: 'all' }
      ]
    },
    { 
      label: 'Subscription', 
      icon: CreditCard, 
      to: '/vendor/subscription-plans', 
      single: true, 
      permission: 'all',
      highlight: true
    },
    { 
      label: 'Security', 
      icon: Shield, 
      to: '/vendor/security', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Billing', 
      icon: Receipt, 
      to: '/vendor/billing', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Notifications', 
      icon: Bell, 
      to: '/vendor/notifications', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Team & Roles', 
      icon: Users, 
      to: '/users-roles', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Messages', 
      icon: MessageSquare, 
      to: '/vendor/messages', 
      single: true, 
      permission: 'all' 
    },
    { 
      label: 'Terms & Policies', 
      icon: FileText, 
      to: '/terms-policies', 
      single: true, 
      permission: 'all' 
    }
  ];

  const navigation = isAdmin() ? adminNavigation : vendorNavigation;

  // Render section header
  const renderSectionHeader = (label) => (
    <li key={`section-${label}`} className="pt-4 pb-1 px-4 first:pt-0">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        {label}
      </span>
    </li>
  );

  const renderNavItem = (item, index) => {
    // Handle section headers
    if (item.type === 'section') {
      return renderSectionHeader(item.label);
    }

    // iter-RBAC — Granular Role-Gated navigation for admin/super_admin.
    // `hasGranularAccess()` is a no-op (always true) for super admins and
    // for any admin never explicitly assigned a Custom Role — so every
    // pre-existing admin/super_admin account keeps seeing exactly what it
    // always has. It only ever restricts a genuine Custom Role assignment
    // (Team & Roles > Roles & Permissions). Vendor nav items never carry
    // a `key`, so this is a complete no-op for vendors too.
    //
    // `superAdminOnly` items (Team & Roles, Settings, Integrations,
    // Security, Finance & Accounts, Developer Tools, and specific
    // sub-items) hard-block plain admin_staff accounts that were NEVER
    // assigned a Custom Role — unchanged legacy behavior. For a
    // restricted Custom Role team member, that hard block is skipped in
    // favor of their actual granular grant, since a super admin may have
    // explicitly handed them that exact module/sub-permission via the
    // Role builder (e.g. "System Docs" role → settings.documentation.view).
    const superAdminHardBlocked = item.superAdminOnly && !isSuperAdmin && !isRestrictedTeamMember;
    if (superAdminHardBlocked) {
      return null;
    }
    if (isAdmin() && item.key && item.single && !hasGranularAccess(item.key, item.sub)) {
      return null;
    }

    const Icon = item.icon;

    if (item.single) {
      const active = isActive(item.to);
      return (
        <PermissionGate permission={item.permission || 'all'} hideOnDenied={true} key={item.to}>
          <li>
            <Link
              to={item.to}
              className={`no-brand flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg transition-all ${
                active
                  ? 'bg-gray-900 text-white'
                  : item.highlight 
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Icon className={`h-5 w-5 flex-shrink-0 ${item.highlight && !active ? 'text-blue-600' : ''}`} />
                <span className="font-medium truncate" title={item.label}>{item.label}</span>
              </div>
              {item.badge && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 flex-shrink-0">2</span>
              )}
            </Link>
          </li>
        </PermissionGate>
      );
    }

    // Expandable menu item — filter children by granular sub-permission
    // for restricted Custom Role team members (no-op / all children pass
    // through unchanged for super admins and unrestricted admins).
    const visibleChildren = (item.children || []).filter((child) => {
      if (child.superAdminOnly && !isSuperAdmin && !isRestrictedTeamMember) return false;
      if (isAdmin() && item.key && !hasGranularAccess(item.key, child.sub)) return false;
      return true;
    });

    // A restricted team member with zero visible children inside this
    // group never sees the parent group at all.
    if (isAdmin() && item.key && visibleChildren.length === 0) {
      return null;
    }

    const isSectionActive = activeSection === item.key;
    const hasActiveChild = visibleChildren.some(child => isActive(child.to));

    return (
      <PermissionGate permission={item.permission || 'all'} hideOnDenied={true} key={item.to || `${item.key}-${index}`}>
        <li>
          <button
            onClick={() => toggleSection(item.key)}
            className={`no-brand flex items-center justify-between w-full gap-3 px-4 py-2.5 rounded-lg transition-all ${
              hasActiveChild
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium truncate" title={item.label}>{item.label}</span>
            </div>
            {isSectionActive ? (
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            )}
          </button>
          {isSectionActive && (
            <ul className="mt-1 ml-4 pl-4 border-l border-gray-200 space-y-1">
              {visibleChildren.map((child) => (
                <PermissionGate permission={child.permission || 'all'} hideOnDenied={true} key={child.to}>
                  <li>
                    <Link
                      to={child.to}
                      data-testid={child.testid}
                      className={`no-brand flex items-center justify-between gap-2 py-2 px-3 rounded-md text-sm transition-all ${
                        isActive(child.to)
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="truncate" title={child.label}>{child.label}</span>
                      {child.badge && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 flex-shrink-0">2</span>
                      )}
                    </Link>
                  </li>
                </PermissionGate>
              ))}
            </ul>
          )}
        </li>
      </PermissionGate>
    );
  };

  return (
    <aside 
      className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 z-20 flex flex-col"
    >
      {/* Logo - Sticky at top */}
      <div className="p-6 border-b border-gray-200 bg-white flex-shrink-0">
        <Link to="/dashboard" className="flex items-center gap-3">
          {branding?.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt="Logo" 
              className="w-10 h-10 object-contain rounded"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center ${branding?.logo_url ? 'hidden' : ''}`}>
            <span className="text-white font-bold text-sm">M</span>
          </div>
          <span className="text-xl font-bold text-gray-900">
            {branding?.portal_title || 'Marketplace'}
          </span>
        </Link>
      </div>

      {/* Navigation - Scrollable area */}
      <nav 
        ref={sidebarRef}
        onScroll={handleScroll}
        className="p-4 pb-6 flex-1 overflow-y-auto"
      >
        <ul className="space-y-1">
          {navigation.map((item, index) => (
            <React.Fragment key={item.to || item.key || `section-${index}`}>
              {renderNavItem(item, index)}
            </React.Fragment>
          ))}
        </ul>
      </nav>

      {/* Currency Switcher — Footer */}
      <div className="border-t border-gray-200 p-3 relative">
        <button
          onClick={() => setShowCurrencyPicker(!showCurrencyPicker)}
          className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          data-testid="currency-switcher-btn"
        >
          <Globe className="h-3.5 w-3.5 text-gray-400" />
          <span className="font-medium">{currencyInfo.flag} {currency}</span>
          <ChevronDown className={`h-3 w-3 ml-auto transition-transform ${showCurrencyPicker ? 'rotate-180' : ''}`} />
        </button>
        
        {showCurrencyPicker && (
          <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[240px] overflow-y-auto z-50" data-testid="currency-picker-dropdown">
            {SUPPORTED_CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => { setCurrency(c.code); setShowCurrencyPicker(false); }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${
                  currency === c.code ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
                data-testid={`currency-option-${c.code}`}
              >
                <span>{c.flag}</span>
                <span>{c.code}</span>
                <span className="text-gray-400 ml-auto">{c.symbol}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
